import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';

interface ButtonConfig {
  text: string;
  onClick: () => void;
  icon?: React.ReactNode;
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  variant?: 'text' | 'outlined' | 'contained';
  // 押せない状態にする（例: 会計受付済みの「お会計はこちら」）。既定 false で後方互換。
  disabled?: boolean;
}

interface PublicHeaderProps {
  buttons?: ButtonConfig[];
  storeName?: string;
  // ロゴを非表示にする（例: 公開注文画面はロゴをフッターへ移すため false）。
  showLogo?: boolean;
}

const PublicHeader: React.FC<PublicHeaderProps> = ({ buttons = [], storeName, showLogo = true }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <AppBar 
      position="sticky" 
      elevation={1}
      sx={{
        backgroundColor: 'white',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Container maxWidth="lg">
        <Toolbar 
          disableGutters 
          sx={{ 
            flexDirection: 'column',
            py: { xs: 1.5, sm: 1 },
            gap: 2,
          }}
        >
          <Box 
            sx={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            {showLogo ? (
              <Box
                sx={{
                  display: 'flex',
                  // 余白なしロゴと ORDER を下端で揃える（中央揃えだと ORDER が浮いて見える）。
                  alignItems: 'flex-end',
                }}
              >
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL || ''}/lp/feast-logo.svg`}
                  alt="FEAST"
                  sx={{
                    height: { xs: '24px', sm: '28px' },
                    width: 'auto',
                    display: 'block',
                  }}
                />
                <Typography
                  variant="h6"
                  component="span"
                  sx={{
                    color: '#F46B4D',
                    fontWeight: 700,
                    fontSize: { xs: '18px', sm: '21px' },
                    lineHeight: 1,
                    // flex-end はテキストボックス下端（ディセンダー含む）で揃うため、
                    // ディセンダー分だけ沈めて FEAST のベースラインに乗せる
                    transform: 'translateY(0.12em)',
                    ml: 1,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                  }}
                >
                  Order
                </Typography>
              </Box>
            ) : (
              // ロゴを隠す場合も space-between のため左側にプレースホルダを置き、店舗名を右に保つ。
              <Box />
            )}

            {storeName && (
              <Typography 
                variant="h6" 
                component="span"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: { xs: '16px', sm: '18px' },
                  textAlign: 'right',
                }}
              >
                {storeName}
              </Typography>
            )}
          </Box>

          {buttons.length > 0 && (
            <Box 
              sx={{ 
                display: 'flex',
                gap: 1.5,
                width: '100%',
                justifyContent: 'flex-end',
              }}
            >
              {buttons?.map((button, index) => (
                <Button
                  key={index}
                  onClick={button.onClick}
                  disabled={button.disabled}
                  color={button.color || 'primary'}
                  variant={button.variant || 'contained'}
                  startIcon={button.icon}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    minWidth: 'auto',
                    px: 2,
                    whiteSpace: 'nowrap',
                    boxShadow: button.variant === 'contained' ? 1 : 'none',
                    '&:hover': {
                      boxShadow: button.variant === 'contained' ? 2 : 'none',
                    },
                  }}
                >
                  {button.text}
                </Button>
              ))}
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default PublicHeader;
